import React, { memo, useCallback, useEffect, useState } from 'react';

import { JitDataGroupStatus } from '@ariksa/jit/api';
import { Stack } from '@chakra-ui/react';
import { each } from 'lodash';
import { useDispatch } from 'react-redux';

import { Card } from 'components/index';
import { getDataGroupsSensitivityQuery } from 'containers/Jit/Components/dataGroupSensitivityUtils';
import { actions } from 'containers/Jit/DataCatalog/slice';
import { useInjector } from 'utils/inject';

import { CatalogSummary } from './components/CatalogSummary';
import { CatalogTable } from './components/CatalogTable';
import { DataSourceTable } from './components/DataSourceTable';
import { viewDataCatalogByOptions } from './components/utils';
import { dataCatalogSaga } from './saga';
import { reducer, sliceKey } from './slice';

export const DataCatalog = memo(() => {
  useInjector(sliceKey, reducer, dataCatalogSaga);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewBy, setViewBy] = useState(viewDataCatalogByOptions[0]);
  const [status, setStatus] = useState<Record<string, JitDataGroupStatus>>({});
  const dispatch = useDispatch();

  const getDataGroups = useCallback(() => {
    dispatch(
      actions.getDataGroups({
        q: {},
        onSuccess: res => {
          // Sensitivity for every data group comes from one bulk request.
          const q = getDataGroupsSensitivityQuery(
            (res?.data_groups ?? []).map(dataGroup => dataGroup?.uuid),
          );
          if (!q.dataGroupSensitivityRequest.data_group_ids.length) return;
          dispatch(actions.getDataGroupsSensitivity({ q }));
        },
      }),
    );
    dispatch(
      actions.getDataGroupStatuses({
        q: {},
        onSuccess: res => {
          if (!res?.data_groups?.length) return;
          let items: Record<string, JitDataGroupStatus> = {};
          each(res?.data_groups, o => (items[o?.data_group_id] = o));
          setStatus(items);
        },
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    getDataGroups();
  }, [getDataGroups]);

  return (
    <Card>
      <Stack spacing={6} h="full" w="full">
        <CatalogSummary
          showCards
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          viewBy={viewBy}
          onViewByChange={setViewBy}
        />

        {viewBy?.value === 'data-source' ? (
          <DataSourceTable searchTerm={searchTerm} />
        ) : (
          <CatalogTable
            searchTerm={searchTerm}
            status={status}
            getDataGroups={getDataGroups}
          />
        )}
      </Stack>
    </Card>
  );
});
